import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { StickyNote, ChevronDown, Copy, Check } from "lucide-react";

// Private, browser-persisted scratchpad for the learner. Never sent to the backend / never coached.
export default function ScratchSpace({ storageKey, className = "mt-6", defaultOpen = false, hideHeader = false }) {
  const [open, setOpen] = useState(defaultOpen || hideHeader);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    loaded.current = false;
    try {
      setText(localStorage.getItem(storageKey) || "");
    } catch (e) {
      setText("");
    }
    loaded.current = true;
  }, [storageKey]);

  useEffect(() => {
    if (!loaded.current) return;
    try {
      localStorage.setItem(storageKey, text);
    } catch (e) {
      /* ignore storage errors */
    }
  }, [text, storageKey]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* clipboard blocked; no-op */
    }
  };

  const words = text.trim() ? text.trim().split(/\s+/).length : 0;

  return (
    <div className={`${className} rounded-md border border-dashed border-stone-300 bg-stone-50/60`} data-testid="scratch-space">
      {!hideHeader && (
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="scratch-space-toggle"
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-stone-500">
          <StickyNote className="h-4 w-4" />
          Scratch space
          {text.trim() && !open && (
            <span className="ml-1 rounded-full bg-stone-200 px-1.5 py-px text-[9px] normal-case tracking-normal text-stone-500">
              has notes
            </span>
          )}
        </span>
        <span className="flex items-center gap-2 text-[11px] text-stone-400">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      )}

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <p className="mb-2 text-[12px] leading-relaxed text-stone-500">
                A space to stash sentences, try out wording, or hold parts of your draft you&rsquo;re not
                ready to use yet.
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="scratch-space-textarea"
                placeholder="Park ideas or draft freely here&hellip;"
                rows={6}
                className="min-h-[120px] w-full resize-y rounded-sm border border-stone-200 bg-white px-3 py-2 text-[14px] leading-relaxed text-stone-700 focus:outline-none focus:ring-1 focus:ring-[#8C3A2A]/40"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-stone-400" data-testid="scratch-space-count">
                  {words} {words === 1 ? "word" : "words"} &middot; saved on this device
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setText("")}
                    data-testid="scratch-space-clear"
                    disabled={!text}
                    className="text-[12px] text-stone-400 hover:text-stone-600 disabled:opacity-40"
                  >
                    Clear
                  </button>
                  <button
                    onClick={copy}
                    data-testid="scratch-space-copy"
                    disabled={!text.trim()}
                    className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-[12px] font-medium text-stone-600 transition-colors hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
